import { useEffect, useRef, useCallback, useState } from 'react';
import { useAuth } from './useAuth';

interface UseIdleTimeoutOptions {
  timeout: number; // in milliseconds
  warningTime?: number; // in milliseconds - when to show warning
  onIdle?: () => void;
  onActive?: () => void;
  onWarning?: () => void;
  events?: string[];
}

const DEFAULT_EVENTS = [
  'mousedown',
  'mousemove',
  'keypress',
  'scroll',
  'touchstart',
  'click',
];

export const useIdleTimeout = ({
  timeout = 15 * 60 * 1000, // 15 minutes default
  warningTime = 30 * 1000, // 30 seconds warning by default
  onIdle,
  onActive,
  onWarning,
  events = DEFAULT_EVENTS,
}: UseIdleTimeoutOptions) => {
  const { logout, isAuthenticated } = useAuth();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isIdleRef = useRef(false);
  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);

  const resetTimeout = useCallback((hideWarning = true) => {
    // Clear existing timeouts and intervals
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }

    // Hide warning if it was showing (only when explicitly requested)
    if (hideWarning) {
      setShowWarning(false);
      setTimeRemaining(0);
    }

    // If timeout is 0 or disabled, don't set any timeouts
    if (timeout <= 0) {
      return;
    }

    // Set warning timeout
    const warningTimeout = timeout - warningTime;
    if (warningTimeout > 0) {
      warningTimeoutRef.current = setTimeout(() => {
        if (isAuthenticated && !isIdleRef.current) {
          setShowWarning(true);
          setTimeRemaining(Math.ceil(warningTime / 1000));
          onWarning?.();
          
          // Start countdown interval - decrement every second
          countdownIntervalRef.current = setInterval(() => {
            setTimeRemaining((prev) => {
              const newTime = prev - 1;
              if (newTime <= 0) {
                // Countdown reached 0, logout automatically
                if (countdownIntervalRef.current) {
                  clearInterval(countdownIntervalRef.current);
                }
                setShowWarning(false);
                isIdleRef.current = true;
                onIdle?.();
                logout();
                return 0;
              }
              return newTime;
            });
          }, 1000);
        }
      }, warningTimeout);
    }

    // Set logout timeout as fallback
    timeoutRef.current = setTimeout(() => {
      if (isAuthenticated && !isIdleRef.current) {
        isIdleRef.current = true;
        onIdle?.();
        logout();
      }
    }, timeout);
  }, [timeout, warningTime, isAuthenticated, onIdle, onWarning, logout]);

  const handleActivity = useCallback(() => {
    if (isIdleRef.current) {
      isIdleRef.current = false;
      onActive?.();
    }
    // Don't reset timer if warning is showing - user must consciously choose to stay logged in
    if (showWarning) {
      return;
    }
    resetTimeout(true);
  }, [resetTimeout, onActive, showWarning]);

  const handleStayLoggedIn = useCallback(() => {
    // Clear countdown interval when user chooses to stay logged in
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }
    setShowWarning(false);
    setTimeRemaining(0);
    resetTimeout(true); // Hide warning and reset timeout
  }, [resetTimeout]);

  const handleLogoutNow = useCallback(() => {
    setShowWarning(false);
    logout();
  }, [logout]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    if (timeout <= 0) {
      return;
    }

    // Add event listeners
    events.forEach((event) => {
      document.addEventListener(event, handleActivity, true);
    });

    // Start the timeout only if not already active
    if (!timeoutRef.current && !warningTimeoutRef.current) {
      resetTimeout();
    }

    // Cleanup
    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity, true);
      });
      
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, [isAuthenticated, events, handleActivity, resetTimeout, timeout, warningTime]);

  return {
    resetTimeout,
    isIdle: isIdleRef.current,
    showWarning,
    timeRemaining,
    handleStayLoggedIn,
    handleLogoutNow,
  };
};


